import { store } from "../store/store";
import {
  registerBillAuditChildWindow,
  broadcastBillAuditToChildren,
} from "./sessionCookie";

function parseStoredToken(raw: string | null): string {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === "string" ? parsed : trimmed;
  } catch {
    return trimmed;
  }
}

export function getStoredAccessToken(): string {
  const fromRedux = store.getState().auth.accessToken;
  if (fromRedux) {
    return parseStoredToken(String(fromRedux));
  }

  const edgeProToken = localStorage.getItem("edgeProAccessToken");
  if (edgeProToken) {
    return parseStoredToken(edgeProToken);
  }

  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {
    return parseStoredToken(accessToken);
  }

  return "";
}

function postToBillAuditChild(
  child: Window,
  childOrigin: string,
  message: Record<string, unknown>,
): void {
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const targetOrigin = isLocal ? "*" : childOrigin;

  try {
    if (!child.closed) {
      child.postMessage(message, targetOrigin);
    }
  } catch {
    try {
      if (!child.closed) {
        child.postMessage(message, "*");
      }
    } catch {
      // no-op
    }
  }
}

/** Push session token to a Bill Audit tab opened from G7 Pro. */
export function pushTokenToBillAuditChild(
  child: Window,
  childOrigin: string,
): void {
  const respond = () => ({
    type: "TOKEN_RESPONSE" as const,
    token: getStoredAccessToken(),
    source: "gsevenpro" as const,
  });

  let attempts = 0;
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const pollMs = isLocal ? 150 : 250;
  const maxAttempts = isLocal ? 40 : 30;

  const send = () => {
    attempts += 1;
    postToBillAuditChild(child, childOrigin, respond());

    if (getStoredAccessToken() && attempts >= 2) {
      window.clearInterval(timer);
      return;
    }
    if (attempts >= maxAttempts || child.closed) {
      window.clearInterval(timer);
    }
  };

  send();
  const timer = window.setInterval(send, pollMs);
}

export function isBillAuditOrigin(origin: string): boolean {
  const normalized = String(origin || "").toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes("billaudit") ||
    normalized.includes("localhost") ||
    normalized.includes("127.0.0.1")
  );
}

export function openBillAuditWindow(url: string | null | undefined): void {
  if (!url) return;

  let openUrl = url;
  const token = getStoredAccessToken();

  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has("parent_source")) {
      parsed.searchParams.set("parent_source", "gsevenpro");
    }
    if (!parsed.searchParams.has("platform")) {
      parsed.searchParams.set("platform", "G7");
    }
    if (!parsed.searchParams.has("parent_origin") && window.location?.origin) {
      parsed.searchParams.set("parent_origin", window.location.origin);
    }
    if (token) {
      const hash = new URLSearchParams();
      hash.set("token", token);
      parsed.hash = hash.toString();
    }
    openUrl = parsed.toString();
  } catch {
    return;
  }

  let childOrigin = "*";
  try {
    childOrigin = new URL(openUrl).origin;
  } catch {
    return;
  }

  const child = window.open(openUrl, "_blank");
  if (!child) return;

  registerBillAuditChildWindow(child, childOrigin);

  if (token) {
    pushTokenToBillAuditChild(child, childOrigin);
  }
}

export function setupBillAuditTokenBridge(): () => void {
  const handleMessage = (event: MessageEvent) => {
    if (!isBillAuditOrigin(event.origin)) return;
    if (event.data?.type === "SESSION_LOGOUT") return;
    if (event.data !== "getToken") return;

    const source = event.source as Window | null;
    if (!source) return;

    registerBillAuditChildWindow(source, event.origin);

    const respond = (tokenValue: string) => {
      source.postMessage(
        {
          type: "TOKEN_RESPONSE",
          token: tokenValue || "",
          source: "gsevenpro",
        },
        event.origin,
      );
    };

    const token = getStoredAccessToken();
    respond(token);
    if (token) return;

    let attempts = 0;
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const pollMs = isLocal ? 150 : 250;
    const maxAttempts = isLocal ? 20 : 24;

    const retryTimer = window.setInterval(() => {
      attempts += 1;
      const nextToken = getStoredAccessToken();
      respond(nextToken);
      if (nextToken || attempts >= maxAttempts) {
        window.clearInterval(retryTimer);
      }
    }, pollMs);
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}

/** Notify open Bill Audit tabs that G7 Pro has an active session (after login). */
export function broadcastBillAuditSession(): void {
  const token = getStoredAccessToken();
  if (!token) return;

  broadcastBillAuditToChildren({
    type: "TOKEN_RESPONSE",
    token,
    source: "gsevenpro",
  });
}

export function buildBillAuditReturnUrl(
  returnUrl: string,
  token?: string | null,
): string {
  try {
    const parsed = new URL(returnUrl);
    if (!parsed.searchParams.has("parent_source")) {
      parsed.searchParams.set("parent_source", "gsevenpro");
    }
    if (!parsed.searchParams.has("platform")) {
      parsed.searchParams.set("platform", "G7");
    }
    if (!parsed.searchParams.has("parent_origin") && window.location?.origin) {
      parsed.searchParams.set("parent_origin", window.location.origin);
    }
    const normalized = token ? parseStoredToken(String(token)) : getStoredAccessToken();
    if (normalized) {
      const hash = new URLSearchParams();
      hash.set("token", normalized);
      parsed.hash = hash.toString();
    }
    return parsed.toString();
  } catch {
    return returnUrl;
  }
}
